import { createApi } from '@reduxjs/toolkit/query/react';
import { client } from '../../../api/NestClient';
import { nestBaseQuery } from '../../../utils/nestBaseQuery';
import type { components, operations, paths } from '../../../api/types/api';

export type AccountsExtractRequestDto =
  paths['/api/v1/imports/excel/extract/accounts']['post']['requestBody']['content']['application/json'];
export type AccountsExtractResponseDto =
  paths['/api/v1/imports/excel/extract/accounts']['post']['responses']['200']['content']['application/json'];
export type AccountsImportRequestDto =
  paths['/api/v1/imports/accounts/insert']['post']['requestBody']['content']['application/json'];
export type AccountsImportResponseDto =
  paths['/api/v1/imports/accounts/insert']['post']['responses']['200']['content']['application/json'];
export type SaveAccountsDraftRequestDto = components['schemas']['SaveImportDraftRequestDto'];
export type SaveAccountsDraftResponseDto =
  operations['ImportsController_saveDraft']['responses'][200]['content']['application/json'];
export type GetAccountsDraftResponseDto =
  operations['ImportsController_getAccountDraft']['responses'][200]['content']['application/json'];
export type DeleteAccountsDraftResponseDto =
  operations['ImportsController_deleteDraft']['responses'][200]['content']['application/json'];

export const accountsImportApi = createApi({
  reducerPath: 'accountsImportApi',
  baseQuery: nestBaseQuery,
  endpoints: (builder) => ({
    extractAccountsExcel: builder.mutation<AccountsExtractResponseDto, AccountsExtractRequestDto>({
      query: (arg) =>
        client.POST('/api/v1/imports/excel/extract/accounts', { body: arg }),
    }),
    importAccounts: builder.mutation<AccountsImportResponseDto, AccountsImportRequestDto>({
      query: (arg) =>
        client.POST('/api/v1/imports/accounts/insert', { body: arg }),
    }),
    saveAccountsDraft: builder.mutation<SaveAccountsDraftResponseDto, SaveAccountsDraftRequestDto>({
      query: (arg) =>
        client.POST('/api/v1/imports/drafts/save', {
          body: arg,
        }),
    }),
    getAccountsDraft: builder.query<GetAccountsDraftResponseDto, void>({
      query: () => client.GET('/api/v1/imports/drafts/accounts'),
    }),
    deleteAccountsDraft: builder.mutation<DeleteAccountsDraftResponseDto, void>({
      query: () =>
        client.DELETE('/api/v1/imports/drafts/{importType}', {
          params: { path: { importType: 'ACCOUNT' } },
        }),
    }),
  }),
});

export const {
  useExtractAccountsExcelMutation,
  useImportAccountsMutation,
  useSaveAccountsDraftMutation,
  useLazyGetAccountsDraftQuery,
  useDeleteAccountsDraftMutation,
} = accountsImportApi;

export async function extractAccountsExcel(path: string): Promise<AccountsExtractResponseDto> {
  const result = await nestBaseQuery<AccountsExtractResponseDto>(
    client.POST('/api/v1/imports/excel/extract/accounts', { body: { path } })
  );
  if ('error' in result) {
    throw new Error(result.error.message);
  }
  return result.data;
}

export async function importAccounts(payload: AccountsImportRequestDto): Promise<AccountsImportResponseDto> {
  const result = await nestBaseQuery<AccountsImportResponseDto>(
    client.POST('/api/v1/imports/accounts/insert', { body: payload })
  );
  if ('error' in result) {
    throw new Error(result.error.message);
  }
  return result.data;
}
